import React, { useState } from 'react';
import ProductPage from '../components/ProductPage';
import { AmmTaskFinder } from '../components/AmmTaskFinder';
import { getBoeingCoverage } from '../data/acousticCameras';

const DATASHEET_URL = '/docs/ctrl-systems-h-series-datasheet.pdf';

const H_SERIES_IMAGES = [
  'https://static.wixstatic.com/media/571718_587034860df449119866a3f97c96fcce~mv2.png/v1/fill/w_720,h_547,al_c,q_85,enc_auto/571718_587034860df449119866a3f97c96fcce~mv2.png',
  'https://static.wixstatic.com/media/571718_80291b29d1284784bc721c034ca1c2cb~mv2.png/v1/fill/w_720,h_583,al_c,q_85,enc_auto/571718_80291b29d1284784bc721c034ca1c2cb~mv2.png',
  'https://static.wixstatic.com/media/571718_8f9757b094bb4679a3c2b4a5a4b2de6f~mv2.png/v1/fill/w_720,h_643,al_c,q_85,enc_auto/571718_8f9757b094bb4679a3c2b4a5a4b2de6f~mv2.png',
];

// Shared feature/application content — H4 and H6 use the same underlying
// acoustic imaging platform; the primary difference is microphone density,
// working distance, and flagship-tier extras on the H6.
const SHARED_FEATURES = [
  {
    title: 'High-Density MEMS Digital Microphone Array',
    description:
      'Spiral array captures the faintest ultrasonic signals — small pinhole leaks and minor discharges become visible long before they escalate. H4 delivers a compact, high-density array for portable use; H6 upgrades to the flagship 162-microphone configuration for maximum sensitivity.',
  },
  {
    title: 'Acoustic Focus Mode',
    description:
      'Filters background noise and interference so inspectors can zero in on a specific area of interest — critical on a busy ramp or in a running-engine bay.',
  },
  {
    title: 'Dual-Function: Leak Evaluation + Partial Discharge',
    description:
      'One handheld tool for both pressurized-gas leaks and partial discharge on electrical assets — pneumatic, hydraulic, and electrical inspections without a tool change.',
  },
  {
    title: 'Leak Evaluation (LQ) Mode',
    description:
      'Automatically identifies leak points and calculates annual energy cost so repairs get prioritized by financial impact rather than guesswork.',
  },
  {
    title: 'Partial Discharge (PD) Mode with PRPD Diagrams',
    description:
      'Automated diagnosis of surface, floating, and tip (corona) discharges with 50/60 Hz AC adaptation — essential for transformers, switchgear, and aviation GSE electrical bays.',
  },
  {
    title: 'On-Device Analysis',
    description:
      'Review and analyze acoustic images directly on the camera — no laptop hand-off required to confirm findings at the aircraft.',
  },
  {
    title: 'AnalyzIR Professional Software',
    description:
      'Full-featured thermal + acoustic analysis and reporting suite for documenting findings, generating work orders, and trending results across the fleet.',
  },
  {
    title: '13 MP Camera + 5" Gorilla Anti-Explosion Display',
    description:
      '1280 × 720 industrial LCD with anti-explosion cover — clear in direct sunlight, rugged for the hangar floor.',
  },
  {
    title: 'LED Flash Lamp — Torch + Flash Modes',
    description:
      'Built-in illumination for shadowed structural pockets, night-shift ramp work, and dim GSE cabinets.',
  },
  {
    title: 'Dual-Battery Swappable Runtime',
    description:
      '2 field-replaceable batteries with ≥4 h continuous operation per pack — enough for a full shift of line-side work.',
  },
  {
    title: 'IP54 Rugged Enclosure',
    description:
      'Dust- and splash-resistant for real MRO environments; operating range -4 to 122 °F.',
  },
];

const SHARED_APPLICATIONS = [
  'Compressed air and bleed-air system leak detection',
  'Cabin pressurization pre-flight and post-flight troubleshooting',
  'Pneumatic, hydraulic, and fuel line acoustic surveys',
  'Landing gear strut nitrogen leak checks',
  'Partial discharge inspection of GPUs and ground-support electrical gear',
  'Switchgear and transformer PD diagnostics in hangar electrical rooms',
  'Compressed air system energy audits across MRO shops',
  'Engine bleed-duct and valve leak investigations',
  'AOG response for hard-to-locate acoustic faults',
  'Fleet-wide PD trend monitoring and asset-condition tracking',
];

const H6_SPECS = [
  { label: 'Model', value: 'FOTRIC H6' },
  { label: 'Microphone Channels', value: '162 MEMS digital microphones' },
  { label: 'Array Layout', value: '6.6-inch spiral topology' },
  { label: 'Acoustic Image FOV', value: '66° × 52°' },
  { label: 'Acoustic Sampling Rate', value: '200 kHz' },
  { label: 'Acoustic Refresh Rate', value: '25 Hz' },
  { label: 'Frequency Range', value: '2 – 100 kHz (sound pressure range varies by frequency band; -25 to 120 dB SPL @ 25 kHz)' },
  { label: 'Working Distance', value: '0.3 – 200 m (1 – 626 ft)' },
  { label: 'Detection Modes', value: 'LQ (Leak) — leakage level display; PD — PRPD diagram, 50/60 Hz adaptive' },
  { label: 'On-Device Analysis', value: 'Direct on-camera acoustic image analysis' },
  { label: 'Analysis Software', value: 'AnalyzIR — professional thermal + acoustic analysis' },
  { label: 'Leak Evaluation', value: 'Auto-identification of leak points + annual energy cost calculation' },
  { label: 'Partial Discharge Diagnostics', value: 'Auto-diagnosis: surface, floating, tip (corona)' },
  { label: 'T-FFTD®', value: 'Captures instantaneous sound signals; extended real-time image retention' },
  { label: 'Digital Zoom', value: '1 – 10× continuous' },
  { label: 'Hologram Mode', value: 'Adjustable sound-pressure threshold overlay on optical image' },
  { label: 'Palettes', value: 'Red-Blue, Iron, Grey — with transparency adjustment' },
  { label: 'Display', value: '5" 1280 × 720 LCD, Gorilla anti-explosion touchscreen' },
  { label: 'Digital Camera', value: '13 Megapixel industrial-grade' },
  { label: 'LED Flash Lamp', value: 'Torch + flash modes' },
  { label: 'Battery', value: '7.4 V, 3500 mAh rechargeable Li-ion, field-replaceable (2 packs included)' },
  { label: 'Battery Runtime', value: '≥ 4 hours per pack (continuous)' },
  { label: 'Battery Charging', value: 'Charge to 90% in 2.5 h — charging dock + USB direct charging' },
  { label: 'External Power', value: 'DC 12 V supported' },
  { label: 'Storage Card', value: 'SD, hot-swappable, up to 1 TB' },
  { label: 'Image Format', value: 'JPEG (holographic acoustic image)' },
  { label: 'Video Format', value: 'MP4 (non-holographic acoustic video, view-only)' },
  { label: 'Voice Annotation', value: 'Up to 120 s per acoustic image' },
  { label: 'Text / Tags / QR', value: 'Soft-keyboard text, tags, and QR/bar code scanning' },
  { label: 'Wi-Fi', value: '2.4 GHz / 5 GHz, 802.11 a/b/g/n/ac' },
  { label: 'Bluetooth', value: 'Supported (BT headset audio monitoring)' },
  { label: 'USB', value: 'USB-C, USB 3.0/2.0, OTG supported' },
  { label: 'HDMI', value: 'Micro HDMI 1.4, 1080p @ 60 Hz streaming' },
  { label: 'FTP Data Transfer', value: 'Via Wi-Fi or device Wi-Fi hotspot' },
  { label: 'GPS', value: 'BeiDou / GPS / GLONASS; location saved to image' },
  { label: 'Compass', value: '360° orientation saved to image' },
  { label: 'Real-time Distance Measurement', value: 'Distance to sound source computed on-device' },
  { label: 'Enclosure Rating', value: 'IP54 (IEC 60529)' },
  { label: 'Operating Temperature', value: '-20 to 44 °C (-4 to 112 °F)' },
  { label: 'Storage Temperature', value: '-40 to 70 °C (-40 to 158 °F) without battery' },
  { label: 'Relative Humidity', value: '< 95 % RH' },
  { label: 'Shock', value: '25 g (IEC 60068-2-27:2008)' },
  { label: 'Vibration', value: '2 g (IEC 60068-2-6:1995)' },
  { label: 'Safety', value: 'SELV (IEC 60950-1:2005); RoHS compliant' },
  { label: 'EMC', value: 'IEC 61000-4-2' },
  { label: 'Weight (with battery)', value: '1.3 kg (2.86 lb); battery 0.2 kg (0.44 lb)' },
  { label: 'Dimensions', value: '175 × 151 × 95 mm' },
  { label: 'Recommended Calibration', value: '2 years' },
  { label: 'Warranty', value: '2 years' },
  { label: 'Languages', value: 'English, Spanish, German' },
  { label: 'Packaging', value: 'Camera + 2× Li-ion batteries, charging dock, USB-C cable, Micro HDMI cable, 128 GB SD + reader, power adapter, softbag, hard carrying case, quick-start manual, certificates' },
];

const H4_SPECS = [
  { label: 'Model', value: 'FOTRIC H4' },
  { label: 'Microphone Channels', value: '112 MEMS digital microphones' },
  { label: 'Acoustic Image FOV', value: '66° × 52°' },
  { label: 'Acoustic Sampling Rate', value: '200 kHz' },
  { label: 'Acoustic Refresh Rate', value: '25 Hz' },
  { label: 'Frequency Range', value: '2 – 100 kHz (sound pressure range varies by frequency band; -19 to 120 dB SPL @ 25 kHz)' },
  { label: 'Working Distance', value: '0.3 – 200 m (1 – 626 ft)' },
  { label: 'Detection Modes', value: 'LQ (Leak) — leakage level display; PD — PRPD diagram, 50/60 Hz adaptive' },
  { label: 'On-Device Analysis', value: 'Direct on-camera acoustic image analysis' },
  { label: 'Analysis Software', value: 'AnalyzIR — professional thermal + acoustic analysis' },
  { label: 'Leak Evaluation', value: 'Auto-identification of leak points + annual energy cost calculation' },
  { label: 'Partial Discharge Diagnostics', value: 'Auto-diagnosis: surface, floating, tip (corona)' },
  { label: 'T-FFTD®', value: 'Captures instantaneous sound signals; extended real-time image retention' },
  { label: 'Digital Zoom', value: '1 – 10× continuous' },
  { label: 'Hologram Mode', value: 'Adjustable sound-pressure threshold overlay on optical image' },
  { label: 'Palettes', value: 'Red-Blue, Iron, Grey — with transparency adjustment' },
  { label: 'Display', value: '5" 1280 × 720 LCD, Gorilla anti-explosion touchscreen' },
  { label: 'Digital Camera', value: '13 Megapixel industrial-grade' },
  { label: 'LED Flash Lamp', value: 'Torch + flash modes' },
  { label: 'Battery', value: '7.4 V, 3500 mAh rechargeable Li-ion, field-replaceable (2 packs included)' },
  { label: 'Battery Runtime', value: '≥ 4 hours per pack (continuous)' },
  { label: 'Battery Charging', value: 'Charge to 90% in 2.5 h — charging dock + USB direct charging' },
  { label: 'External Power', value: 'DC 12 V supported' },
  { label: 'Storage Card', value: 'SD, hot-swappable, up to 1 TB' },
  { label: 'Image Format', value: 'JPEG (holographic acoustic image)' },
  { label: 'Video Format', value: 'MP4 (non-holographic acoustic video, view-only)' },
  { label: 'Voice Annotation', value: 'Up to 120 s per acoustic image' },
  { label: 'Text / Tags / QR', value: 'Soft-keyboard text, tags, and QR/bar code scanning' },
  { label: 'Wi-Fi', value: '2.4 GHz / 5 GHz, 802.11 a/b/g/n/ac' },
  { label: 'Bluetooth', value: 'Supported (BT headset audio monitoring)' },
  { label: 'USB', value: 'USB-C, USB 3.0/2.0, OTG supported' },
  { label: 'HDMI', value: 'Micro HDMI 1.4, 1080p @ 60 Hz streaming' },
  { label: 'FTP Data Transfer', value: 'Via Wi-Fi or device Wi-Fi hotspot' },
  { label: 'GPS', value: 'BeiDou / GPS / GLONASS; location saved to image' },
  { label: 'Compass', value: '360° orientation saved to image' },
  { label: 'Real-time Distance Measurement', value: 'Distance to sound source computed on-device' },
  { label: 'Enclosure Rating', value: 'IP54 (IEC 60529)' },
  { label: 'Operating Temperature', value: '-20 to 44 °C (-4 to 112 °F)' },
  { label: 'Storage Temperature', value: '-40 to 70 °C (-40 to 158 °F) without battery' },
  { label: 'Relative Humidity', value: '< 95 % RH' },
  { label: 'Shock', value: '25 g (IEC 60068-2-27:2008)' },
  { label: 'Vibration', value: '2 g (IEC 60068-2-6:1995)' },
  { label: 'Safety', value: 'SELV (IEC 60950-1:2005); RoHS compliant' },
  { label: 'EMC', value: 'IEC 61000-4-2' },
  { label: 'Weight (with battery)', value: '1.3 kg (2.86 lb); battery 0.2 kg (0.44 lb)' },
  { label: 'Dimensions', value: '175 × 151 × 95 mm' },
  { label: 'Recommended Calibration', value: '2 years' },
  { label: 'Warranty', value: '2 years' },
  { label: 'Languages', value: 'English, Spanish, German' },
  { label: 'Packaging', value: 'Camera + 2× Li-ion batteries, charging dock, USB-C cable, Micro HDMI cable, 128 GB SD + reader, power adapter, softbag, hard carrying case, quick-start manual, certificates' },
];

const AcousticCameraHSeriesPage = ({ onRequestDemo, onRequestQuote }) => {
  const [model, setModel] = useState('h6');

  const productData = model === 'h6'
    ? {
        productName: 'FOTRIC H6 High-Performance Acoustic Imaging Camera',
        tagline: '162 MEMS Microphones — Flagship Sensitivity for Leaks & Partial Discharge',
        description:
          'The FOTRIC H6 is the high-performance flagship of the H-Series acoustic imaging line. A 6.6-inch spiral array of 162 MEMS digital microphones delivers exceptional sensitivity for the smallest pressurized-gas leaks and the earliest signs of partial discharge on aviation electrical equipment. Dual LQ (Leak) and PD (Partial Discharge) detection modes, on-device analysis, and the AnalyzIR professional software suite make the H6 a single tool that replaces multiple legacy stethoscopes, air-leak detectors, and PD probes.',
        specifications: H6_SPECS,
        partNumber: 'F00061',
        boeingToolTag: 'COM-18702 (equivalent)',
        boeingCoverage: getBoeingCoverage('h6'),
        compareCameraId: 'h6',
        dataSheetUrl: DATASHEET_URL,
      }
    : {
        productName: 'FOTRIC H4 High-Performance Acoustic Imaging Camera',
        tagline: '112 MEMS Microphones — H-Series Dual-Mode in a Lower Entry Point',
        description:
          'The FOTRIC H4 is the H-Series compact configuration — same acoustic imaging platform, same 5" Gorilla anti-explosion touchscreen, same IP54 rugged body, same dual LQ (leak) and PD (partial discharge) detection modes as the flagship H6, delivered at a lower entry point with a 112 MEMS digital microphone array. Ideal for teams that need H-Series-tier reporting and durability without the 162-mic flagship density.',
        specifications: H4_SPECS,
        partNumber: 'FOTRIC-H4',
        boeingToolTag: 'COM-18702 (equivalent)',
        boeingCoverage: getBoeingCoverage('h4'),
        compareCameraId: 'h4',
        dataSheetUrl: DATASHEET_URL,
      };

  const modelToggle = (
    <div className="mb-6 flex items-center gap-2" data-testid="h-series-model-toggle">
      <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider mr-2">Model:</span>
      <button
        type="button"
        onClick={() => setModel('h4')}
        data-testid="h-series-toggle-h4"
        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
          model === 'h4'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
        aria-pressed={model === 'h4'}
      >
        H4
      </button>
      <button
        type="button"
        onClick={() => setModel('h6')}
        data-testid="h-series-toggle-h6"
        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
          model === 'h6'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
        aria-pressed={model === 'h6'}
      >
        H6
      </button>
    </div>
  );

  return (
    <>
      <ProductPage whereToBuy buyNow={model === 'h6' ? 'F00061' : undefined}
        {...productData}
        image={H_SERIES_IMAGES[0]}
        images={H_SERIES_IMAGES}
        imageContain
        features={SHARED_FEATURES}
        applications={SHARED_APPLICATIONS}
        heroExtra={modelToggle}
        extraSections={<AmmTaskFinder tool="COM-18702" />}
        onRequestDemo={onRequestDemo}
        onRequestQuote={onRequestQuote}
      />
    </>
  );
};

export default AcousticCameraHSeriesPage;
